import {
  Autocomplete,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { fieldsApi, type FieldSummary } from '@/api/fields';
import { getOrganizationLayers } from '@/api/layers';
import type { LayerInfo } from '@/types/layer';

const FIELD_SEARCH_DEBOUNCE_MS = 300;
const FIELD_SEARCH_LIMIT = 40;

interface FieldLayerPrefillSelectsProps {
  selectedLayerId: string;
  onLayerSelect: (layerId: string) => void;
  disabled?: boolean;
}

/**
 * Two-step source picker: searchable field → layer of that field.
 * Used to pre-fill layer template properties from an existing reservoir.
 */
export const FieldLayerPrefillSelects: React.FC<FieldLayerPrefillSelectsProps> = ({
  selectedLayerId,
  onLayerSelect,
  disabled = false,
}) => {
  const [fieldInput, setFieldInput] = useState('');
  const [debouncedFieldInput, setDebouncedFieldInput] = useState('');
  const [fieldOptions, setFieldOptions] = useState<FieldSummary[]>([]);
  const [selectedField, setSelectedField] = useState<FieldSummary | null>(null);
  const [loadingFields, setLoadingFields] = useState(false);
  const [layers, setLayers] = useState<LayerInfo[]>([]);
  const [loadingLayers, setLoadingLayers] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedFieldInput(fieldInput.trim());
    }, FIELD_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [fieldInput]);

  const loadFields = useCallback(async (search: string) => {
    try {
      setLoadingFields(true);
      const response = await fieldsApi.getFieldsList({
        search: search || undefined,
        limit: FIELD_SEARCH_LIMIT,
        skip: 0,
      });
      setFieldOptions(response.fields);
    } catch (err) {
      console.error('Failed to load fields for template prefill:', err);
      setFieldOptions([]);
    } finally {
      setLoadingFields(false);
    }
  }, []);

  useEffect(() => {
    void loadFields(debouncedFieldInput);
  }, [debouncedFieldInput, loadFields]);

  useEffect(() => {
    let cancelled = false;

    const loadLayers = async () => {
      if (!selectedField) {
        setLayers([]);
        return;
      }
      try {
        setLoadingLayers(true);
        const fieldLayers = await getOrganizationLayers(selectedField.id);
        if (!cancelled) {
          setLayers(fieldLayers);
        }
      } catch (err) {
        console.error('Failed to load layers for field:', err);
        if (!cancelled) {
          setLayers([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingLayers(false);
        }
      }
    };

    void loadLayers();
    return () => {
      cancelled = true;
    };
  }, [selectedField]);

  const handleFieldChange = (_: unknown, field: FieldSummary | null) => {
    setSelectedField(field);
    onLayerSelect('');
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={fieldOptions}
          value={selectedField}
          inputValue={fieldInput}
          onInputChange={(_, value, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setFieldInput(value);
            }
          }}
          onChange={handleFieldChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loadingFields}
          disabled={disabled}
          filterOptions={(options) => options}
          noOptionsText={loadingFields ? 'Loading…' : 'No fields found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Field"
              placeholder="Search field…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingFields ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth disabled={disabled || !selectedField || loadingLayers}>
          <InputLabel>Layer</InputLabel>
          <Select
            value={selectedLayerId}
            label="Layer"
            onChange={(e) => onLayerSelect(e.target.value)}
          >
            <MenuItem value="">
              <em>{selectedField ? 'None — fill manually' : 'Select a field first'}</em>
            </MenuItem>
            {layers.map((layer) => (
              <MenuItem key={layer.id} value={layer.id}>
                {layer.name}{layer.type ? ` (${layer.type})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};
